import { template } from "@babel/core";
import { ElementDefinition } from "../definition/element/ElementDefinition";
import { FieldObjectConcreteDefinition } from "../definition/element/FieldObjectConcreteDefinition";
import { DigitalFormBoxDefinition } from "../definition/form/DigitalFormBoxDefinition";
import { DigitalFormDefinition } from "../definition/form/DigitalFormDefinition";
import { DigitalFormBoxTemplate } from "./DigitalFormBoxTemplate";
import { DigitalFormTemplate } from "./DigitalFormTemplate";
import { FieldElementTemplate } from "./FieldElementTemplate";
import { FieldObjectTemplate } from "./FieldObjectTemplate";
import { TemplateManager } from "./TemplateManager";

export type TemplateDefinition = { [keys: string]: FieldElementTemplate };

const getTemplate = (templateDefinition: TemplateDefinition, name: string): FieldElementTemplate => {
    const template = templateDefinition[name];
    if (template) {
        return Object.assign({ id: name }, template);
    }
    throw new Error("undefined field box template: " + name);
}

export function resolveTemplateReference(reference: FieldElementTemplate, templateDefinition?: TemplateDefinition): ElementDefinition[] {
    if (reference.kind === "array") return reference.items.flatMap(item => resolveTemplateReference(item, templateDefinition));
    if (reference.kind === "field") {
        const copy = Object.assign({}, reference) as FieldObjectTemplate;
        copy.fields = copy.fields.flatMap(element => resolveTemplateReference(element, templateDefinition));
        return [copy as FieldObjectConcreteDefinition];
    }
    if (reference.kind !== "template") return [reference];
    const definition = getTemplate(templateDefinition || TemplateManager.templates, reference.template);
    const copy = Object.assign({}, reference);
    delete copy.template;
    delete copy.kind;
    let copiedDefinition = Object.assign({}, definition);
    if (copy.args) {
        let copiedDefinitionString = JSON.stringify(copiedDefinition);
        Object.entries(copy.args).forEach(entry => {
            copiedDefinitionString = copiedDefinitionString.replace(new RegExp(`\\$\\(${entry[0]}\\)`, "g"), entry[1]);
        });
        copiedDefinition = JSON.parse(copiedDefinitionString);
    }
    delete copy.args;
    if (copiedDefinition.kind === "field" && copy.index !== undefined) {
        copiedDefinition.fields = (copiedDefinition.fields as (FieldElementTemplate | ElementDefinition)[]).flatMap(field => {
            if (field.kind === "array") return resolveTemplateReference(reference, templateDefinition);
            const newField = Object.assign({}, field);
            if(field.id) {
                newField.id = `${field.id}_${reference.index}`;
            }
            return resolveTemplateReference(newField, templateDefinition);
        });
        delete copy.index;
    }
    return resolveTemplateReference(Object.assign(copiedDefinition, copy), templateDefinition);
}

export const resolveBoxDefinition = (definition: DigitalFormBoxTemplate, templateDefinition?: TemplateDefinition): DigitalFormBoxDefinition => {
    const copy = Object.assign({}, definition);
    copy.elements = definition.elements.flatMap(element => resolveTemplateReference(element, templateDefinition));
    return copy as DigitalFormBoxDefinition;
}

export const resolveDigitalFormDefinition = (definition: DigitalFormTemplate, templateDefinition?: TemplateDefinition): DigitalFormDefinition => {
    const copy = Object.assign({}, definition);
    copy.boxes = definition.boxes.map(box => resolveBoxDefinition(box, templateDefinition));
    return copy as DigitalFormDefinition;
}